import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { State, Action, Getter, Mutation } from 'vuex-class';

import Field from '@/../bower_components/teknisavuecomponents/assets/interfaces/Field';
import TkForm from '@/../bower_components/teknisavuecomponents/components/TkForm.vue';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import DocumentosController from '@/assets/ts/controller/DocumentosController';
import FileUtils from '@/../bower_components/teknisavuecomponents/assets/utils/FileUtils';
import Util from '@/../bower_components/teknisavuecomponents/assets/utils/Util';

@Component({
  name: 'NovoDocumentoPopup',
  components: {
    TkForm,
  },
})
class NovoDocumentoPopup extends Vue {
  // #region [ PROPS ]
  @Prop({ default: '' })
  private nrCategoria: string;

  @Prop({ default: '' })
  private nrVinculo: string;

  @Prop({ default: undefined })
  private overrideSaveAction: (internalRow: any) => any;

  @Prop({ default: null })
  private row: any;

  @Prop({ default: false })
  private value: boolean;
  // #endregion

  // #region [ EVENTS ]
  @Emit('after-save')
  private emitAfterSave() {}

  @Emit('input')
  private emitInput(value: boolean) {}
  // #endregion

  // #region [ DATA ]
  private internalValue = false;
  private loading = false;

  private internalRow: any = {};

  private categorias = [];
  private tipos = [];

  private categoriasLoaded = false;
  private tiposLoaded = false;

  private base64 = '';
  // #endregion

  // #region [ COMPUTED ]
  private get fields(): Field[] {
    const fields = [
      {
        name: 'DSREFERENCIA',
        label: 'Referência',
        type: 'text',
        size: 12,
        required: true,
      },
      {
        name: 'NRCATEGDOCUMENTO',
        label: 'Categoria',
        type: 'autocomplete',
        descriptionField: 'NMCATEGDOCUMENTO',
        valueField: 'NRCATEGDOCUMENTO',
        items: this.categorias,
        size: 6,
        required: true,
      },
      {
        name: 'NRTIPODOCUMENTO',
        label: 'Tipo',
        type: 'autocomplete',
        descriptionField: 'NMTIPODOCUMENTO',
        valueField: 'NRTIPODOCUMENTO',
        items: this.tipos,
        size: 6,
        required: true,
      },
      {
        name: 'FILE',
        label: 'Documento',
        type: 'file',
        size: 12,
        required: true,
      },
    ];

    return fields;
  }

  private get isValid(): boolean {
    const isValid =
      !!this.base64 &&
      !!this.internalRow.NRCATEGDOCUMENTO &&
      !!this.internalRow.NRTIPODOCUMENTO &&
      !!this.internalRow.DSREFERENCIA;

    return isValid;
  }
  // #endregion

  // #region [ WATCHERS ]
  @Watch('value', { immediate: true })
  private valueOnChange() {
    this.internalValue = this.value;

    if (this.internalValue) {
      this.loadFields();

      if (this.row === null) {
        this.setDefaultRow();
      } else {
        this.internalRow = Util.deepCopy(this.row);
      }
    }
  }
  // #endregion

  // #region [ METHODS ]
  private closeDialog() {
    this.internalValue = false;
    this.emitInput(this.internalValue);
  }

  private onSave() {
    if (!this.isValid) {
      const message = 'Preencha todos os campos obrigatórios!';
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);

      return;
    }

    if (this.overrideSaveAction) {
      this.setDescriptionsInRow();
      this.overrideSaveAction(this.internalRow);
    } else {
      this.save();
    }
  }

  private async save() {
    this.loading = true;

    try {
      const internalRow = {
        ...this.internalRow,
        FILE: this.base64,
      };

      await DocumentosController.saveDocumento(internalRow, false);

      this.closeDialog();
      this.emitAfterSave();
    } finally {
      this.loading = false;
    }

  }

  private async loadFields() {
    this.loading = true;

    try {
      await this.loadCategorias();
      await this.loadTipos();
    } finally {
      this.loading = false;
    }
  }

  private async loadCategorias() {
    if (!this.categoriasLoaded) {
      this.categorias = await DocumentosController.getCategorias(false);
      this.categoriasLoaded = true;
    }
  }

  private async loadTipos() {
    if (!this.tiposLoaded) {
      this.tipos = await DocumentosController.getTipos(false);
      this.tiposLoaded = true;
    }
  }

  private setDefaultRow() {
    let nrCategoria = this.nrCategoria;

    if (nrCategoria === 'null' || !nrCategoria) {
      nrCategoria = '';
    }

    this.internalRow = {
      NRCATEGDOCUMENTO: nrCategoria,
      NRVINCULOM: this.nrVinculo,
      NRTIPODOCUMENTO: '',
    };
  }

  private formOnFieldInput(fieldName: string, value: any) {
    if (fieldName === 'FILE') {
      this.fileFieldOnChange(value);
    }
  }

  private async fileFieldOnChange(value: any) {
    if (value instanceof File) {
      await this.createBase64File(value);
      this.internalRow.NMANEXO = value.name;
    } else {
      this.base64 = '';
      this.internalRow.NMANEXO = null;
    }
  }

  private async createBase64File(file: File) {
    this.loading = true;

    try {
      const fileString = await FileUtils.toBase64(file);
      this.base64 = fileString;
    } catch (err) {
      const message = 'Não foi possível carregar o arquivo';
      SnackbarUtils.showMessage(message, 'alert', 5000);

      throw err;
    } finally {
      this.loading = false;
    }
  }

  private setDescriptionsInRow() {
    const categoriaField = Util.findField(this.fields, 'NRCATEGDOCUMENTO');
    this.internalRow.NMCATEGDOCUMENTO = Util.getDescriptionValue(categoriaField, this.internalRow);

    const tipoField = Util.findField(this.fields, 'NRTIPODOCUMENTO');
    this.internalRow.NMTIPODOCUMENTO = Util.getDescriptionValue(tipoField, this.internalRow);
  }
  // #endregion
}

export default NovoDocumentoPopup;